/**
 * The conventions a flattened email text part carries, undone.
 *
 * A text part produced from HTML carries the flattener's conventions with it:
 * `*bold*` around what was `<b>`, an alt string in `[image: …]` where a picture
 * was, and the anchor's href in angle brackets. None of it is specified — RFC
 * 2046 asks only that the alternatives carry the same information — so this
 * reads Gmail's conventions specifically, which is what mail forwarded into this
 * inbox is flattened by. A client that writes `/italic/` or a bare `ALT [URL]`
 * passes through untouched rather than wrong.
 *
 * Deliberately not markdown. `*x*` is bold here and emphasis there, hard
 * wrapping at 76 characters would be reflowed into paragraphs, and a `--`
 * sign-off separator would promote the line above it to a heading — markdown is
 * the one rendering that shows a message *wrongly* rather than plainly.
 */

/**
 * Strip the scaffolding that only stood in for markup.
 *
 * The alt string is kept and its brackets dropped, so a mail that is one big
 * picture reads as the words its sender chose for it. The picture itself cannot
 * come back: `[image: Logo]` holds a name and nothing else, and the URL
 * beneath it is the href of the anchor that wrapped the image, not the image.
 */
export function unwrapConventions(text: string): string {
  return (
    text
      // A long alt string wraps like any other text, so the placeholder can
      // straddle a line break.
      .replace(/\[image:\s*([^\]]*)\]/g, (_match, alt: string) =>
        collapseSpaces(alt),
      )
      // Angle brackets come from RFC 3986's non-normative advice on delimiting a
      // URI in running text. They are punctuation, and read better linkified
      // without them.
      .replace(/<(https?:\/\/[^>\s]+)>/g, '$1')
      // Dropping a placeholder that stood on its own line leaves the line behind.
      .replace(/^[^\S\n]+$/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
}

export type TextRun = { isBold: boolean; text: string };

/**
 * `*bold*` becomes bold, bounded to a single line.
 *
 * An unmatched asterisk is ordinary in prose — a footnote marker, a censored
 * word — and letting the pair run across line breaks turns the rest of a message
 * bold the moment someone types one.
 */
export function toTextRuns(text: string): Array<TextRun> {
  return text
    .split(/(\*[^*\n]+\*)/g)
    .filter((part) => part !== '')
    .map((part) =>
      BOLD_RUN.test(part)
        ? { isBold: true, text: part.slice(1, -1) }
        : { isBold: false, text: part },
    );
}

const BOLD_RUN = /^\*[^*\n]+\*$/;

function collapseSpaces(value: string) {
  return value.replace(/\s+/g, ' ').trim();
}
